import React, { useEffect, useState, useRef } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { Breadcrumb, Col, Row, Button, Alert, Spinner } from "react-bootstrap";
import { LinkContainer } from "react-router-bootstrap";
import API from "../Utility";
import validator from "validator";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import MultiSelectTypeahead from "../MultiSelectTypeahead";

// Styles
import "./CreateUpdateCampaignView.scss";

export default function CreateUpdateCampaignView({ children, ...routeProps }) {
  let { project, campaignId } = useParams();
  let history = useHistory();
  const location = useLocation();
  let stateCampaign = location && location.state ? location.state.campaign : {};
  const { action } = routeProps;

  const [formMode, setFormMode] = useState(action); // create or update handled here
  const [campaignName, setCampaignName] = useState();

  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);

  const [campaign, setCampaign] = useState(stateCampaign);
  const [isLoading, setIsLoading] = useState(false);
  const [groupsValue, setGroupsValue] = useState([]);
  const [coursesValue, setCoursesValue] = useState([]);
  const [showAllGroups, setShowAllGroups] = useState(false);

  const courseGroupMultiplierLimit = {
    value: 100000,
    displayValue: '100,000'
  };

  const coursesLimit = {
    value: 20,
    displayValue: '20'
  };

  const groupsLimit = {
    value: 1000,
    displayValue: '1000'
  };

  const formRef = useRef();

  async function getCampaignData(query) {
    try {
      setIsLoading(true);
      let apiCampaign = await API.get({
        url: query.url,
      });
      setIsLoading(false);
      return apiCampaign;
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    async function initializeData() {
      let apiCampaign = await getCampaignData({
        url: `/admin/${project}/campaigns/${campaignId}`,
      });

      setCampaign(apiCampaign);
      setCampaignName(apiCampaign?.name);
      if (apiCampaign.groups && apiCampaign.groups.length <= 10) {
        setShowAllGroups(true);
      }
    }
    if (action && action === "update" && !campaign?._id) {
      initializeData();
    } else {
      setCampaignName(campaign.name);
    }
  }, []);

  const loading = () => {
    return !campaign?._id && isLoading ? (
      <div>
        <Spinner animation="border" />
      </div>
    ) : (
      <div>
        <span>Campaign not found</span>
      </div>
    );
  };

  // Initialize dynamic form validation
  let dynamicFormValidation = {};

  // Initialize dynamic form fields
  let dynamicFormFields = {};

  const setInputValidationByFieldType = (currentField) => {
    if (currentField.type === "text" || currentField.type === "textarea") {
      dynamicFormValidation[currentField.fieldName] = Yup.string()
        .max(250, "Must be 250 characters or less")
        .required(
          currentField.isRequired
            ? currentField.fieldLabel + " is required"
            : false
        );
    }
    if (currentField.type === "list") {
      if (currentField.isRequired && currentField.options) {
        let validOptions = currentField.options.map(function(option, key) {
          return option;
        });
        dynamicFormValidation[currentField.fieldName] = Yup.string()
          .oneOf(validOptions, currentField.fieldLabel + " is required")
          .required(
            currentField.isRequired
              ? currentField.fieldLabel + " is required"
              : false
          );
      } else if (currentField.isRequired && !currentField.options) {
        if (currentField.fieldName === 'groups') {
          dynamicFormValidation[currentField.fieldName] = Yup.array()
            .of(Yup.string())
            .max(groupsLimit.value, `The number of groups has exceeded the limit of ${groupsLimit.displayValue}`)
            .required(
              currentField.isRequired
                ? currentField.fieldLabel + " is required"
                : false
            );
        } else if (currentField.fieldName === 'courses') {
          dynamicFormValidation[currentField.fieldName] = Yup.array()
            .of(Yup.string())
            .max(coursesLimit.value, `The number of courses has exceeded the limit of ${coursesLimit.displayValue}`)
            .required(
              currentField.isRequired
                ? currentField.fieldLabel + " is required"
                : false
            );
        }
      } else {
        dynamicFormValidation[currentField.fieldName] = Yup.string();
      }
    }
    if (currentField.type === "date") {
      if (currentField.fieldName === 'endDate') {
        dynamicFormValidation[currentField.fieldName] = Yup.date().min(
          Yup.ref('startDate'), 'End date cannot be before the start date'
        ).required(
          currentField.isRequired
            ? 'A valid ' + currentField.fieldLabel + " is required"
            : false
        );
      } else {
        dynamicFormValidation[currentField.fieldName] = Yup.date().required(
          currentField.isRequired
            ? 'A valid ' + currentField.fieldLabel + " is required"
            : false
        );
      }
    }
  };

  const setInitialFormValues = (currentField, action) => {
    if (action && action === "create") {
      if (currentField.fieldName, currentField.defaultValue) {
        dynamicFormFields[currentField.fieldName] = currentField.defaultValue;
      } else {
        dynamicFormFields[currentField.fieldName] = currentField.type === "list" ? undefined : "";
      }
    } else if (
      action &&
      action === "update" &&
      campaign &&
      (campaign[currentField.fieldName])
    ) {
      if (currentField.type === 'date') {
        dynamicFormFields[currentField.fieldName] = campaign[currentField.fieldName] ? new Date(campaign[currentField.fieldName]).toLocaleDateString('en-ZA').replace(/\//g, "-") : "";
      } else {
        if (currentField.type === 'list') {
          dynamicFormFields[currentField.fieldName] =
            campaign[currentField.fieldName] || "";
          if (currentField.fieldName === 'groups' && groupsValue?.length < 1 && campaign[currentField.fieldName].length > 0) {
            setGroupsValue(campaign[currentField.fieldName]);
          } else if (currentField.fieldName === 'courses' && coursesValue?.length < 1 && campaign[currentField.fieldName].length > 0) {
            setCoursesValue(campaign[currentField.fieldName]);
          }
        } else {
          dynamicFormFields[currentField.fieldName] =
            campaign[currentField.fieldName] || "";
        }
      }
    } else {
      dynamicFormFields[currentField.fieldName] = "";
    }
  };

  const setInputType = (fieldType) => {
    if (fieldType === "password") {
      return "password";
    } else if (fieldType === "email") {
      return "email";
    } else if (fieldType === "date") {
      return "date";
    } else if (fieldType === "list") {
      return "select";
    } else if (fieldType === "telephone") {
      return "tel";
    } else if (fieldType === "boolean") {
      return "radio";
    } else if (fieldType === "textarea") {
      return "textarea";
    } else {
      return "text";
    }
  };

  const setSelectOptions = (field) => {
    return (
      <>
        <option value={null}>Choose a {field.fieldLabel}</option>
        {field.options.map(function(item, index) {
          if (item._id) {
            return (
              <option key={`${field.fieldName}-option-${index}`} value={item._id}>
                {item.name}
              </option>
            );
          } else if (item.value) {
            return (
              <option key={`${field.fieldName}-option-${index}`} value={item.value}>
                {item.label}
              </option>
            );
          } else {
            return (
              <option key={`${field.fieldName}-option-${index}`} value={item}>
                {item}
              </option>
            );
          }
        })}
      </>
    );
  };

  const getInputUserFriendlyValue = (input, fieldValue) => {
    if (input?.options?.length > 0) {
      let index = input.options.findIndex((option) => {
        return option.value === fieldValue;
      });
      if (index !== -1 && input.options[index]) {
        return input.options[index].name || input.options[index].label;
      }
    } else {
      return fieldValue;
    }
  };

  let today = new Date().toLocaleDateString('en-ZA').replace(/\//g, "-");

  let campaignFields = [
    { fieldName: 'name', type: 'text', fieldLabel: 'Campaign Name', isRequired: true },
    {
      fieldName: 'status', type: 'boolean', fieldLabel: 'Status', isRequired: false, options: [{ value: 'draft', name: 'Draft' }, { value: 'published', name: 'Published' }], defaultValue: 'draft',
      displayAsLabelIfDisabled: true
    },
    { fieldName: 'description', type: 'textarea', fieldLabel: 'Description', isRequired: false },
    {
      type: 'fieldGroup',
      colClasses: 'col-xs-12 col-sm-6 col-md-3',
      groupName: 'start-end-dates',
      groupedFields: [
        { fieldName: 'startDate', type: 'date', fieldLabel: 'Start Date', isRequired: true, defaultValue: today },
        { fieldName: 'endDate', type: 'date', fieldLabel: 'End Date', isRequired: true },
      ]
    },
    {
      type: 'fieldGroup',
      colClasses: 'col-xs-12 col-sm-6 col-md-3',
      groupName: 'start-end-dates',
      groupLabel: 'Scoring',
      groupedFields: [
        {
          fieldName: 'goal',
          type: 'list',
          fieldLabel: 'Campaign Goal:',
          isRequired: false,
          options: [
            { value: 'MostCoursesCompleted', label: 'Most courses completed' },
            { value: 'FirstToCompleteAllCourses', label: 'First to complete all courses' },
            { value: 'MostPoints', label: 'Most points accumulated' },
            { value: 'AllCoursesCompleted', label: 'Completion of courses' }
          ],
          defaultValue: 'MostCoursesCompleted',
          displayAsLabelIfDisabled: true
        },
        {
          fieldName: 'type',
          type: 'list',
          fieldLabel: 'Compete Against:',
          isRequired: false,
          options: [
            { value: 'GroupVsGroup', label: 'Groups against each other' },
            { value: 'UserVsUsersAllGroups', label: 'Users against users in all groups' },
            { value: 'UserVsUsersOwnGroup', label: 'Users against users in their group' },
            { value: 'None', label: 'None' }
          ],
          defaultValue: 'GroupVsGroup',
          displayAsLabelIfDisabled: true
        },
        {
          fieldName: 'scoringCalculation',
          type: 'list',
          fieldLabel: 'Calculate Using:',
          isRequired: false,
          options: [
            { value: 'CourseCompletion', label: 'Course completion' },
            { value: 'AssessmentScores', label: 'Assessment scores' },
            { value: 'ProgressPercentage', label: 'Progress %' },
            { value: 'LessonsCompleted', label: 'Lessons viewed/completed' }
          ],
          defaultValue: 'CourseCompletion',
          displayAsLabelIfDisabled: true
        },
        // {
        //   fieldName: 'timeLimit',
        //   type: 'list',
        //   fieldLabel: 'Time Limits:',
        //   isRequired: false,
        //   options: [
        //     { value: 'CampaignWindow', label: 'Limit to campaign time window' },
        //     { value: 'None', label: 'Do not limit' }
        //   ],
        //   defaultValue: 'CampaignWindow',
        //   displayAsLabelIfDisabled: true
        // },
      ]
    },
    { fieldName: 'courses', type: 'list', fieldLabel: 'Course', isRequired: true, url: `/admin/${project}/campaigns/courses-lookup` },
    { fieldName: 'groups', type: 'list', fieldLabel: 'Group', isRequired: true, url: `/admin/${project}/campaigns/groups-lookup` }
  ];

  // Generate dynamic form defaults
  if (campaignFields && campaignFields.length > 0) {
    for (let i = 0; i < campaignFields.length; i++) {
      let currentField = campaignFields[i];
      if (currentField.type === 'fieldGroup') {
        for (let i = 0; i < currentField.groupedFields.length; i++) {
          let currentGroupedField = currentField.groupedFields[i];
          // Generate dynamic form validation
          setInputValidationByFieldType(currentGroupedField);

          // Generate dynamic form fields
          setInitialFormValues(currentGroupedField, action);
        }
      } else {
        // Generate dynamic form validation
        setInputValidationByFieldType(currentField);

        // Generate dynamic form fields
        setInitialFormValues(currentField, action);
      }
    }
  }

  const handleChange = (e) => {
    // console.log(e);
  };

  const dateHasPassed = (date) => {
    const todaysDate = new Date();
    const dateToCheck = new Date(date);

    todaysDate.setHours(0, 0, 0, 0);
    dateToCheck.setHours(0, 0, 0, 0);

    return dateToCheck <= todaysDate;
  };

  // TODO: Adjust this list once we add more capabilities
  const alwaysReadonlyList = ['goal', 'type', 'scoringCalculation', 'timeLimit'];

  // Define the list of fields that are not editable once a campaign has been published
  if (action === 'update' && dynamicFormFields?.status === 'published') {
    // Scoring options can NOT be edited
    if (!alwaysReadonlyList.includes('goal')) {
      alwaysReadonlyList.push('goal');
    }
    if (!alwaysReadonlyList.includes('type')) {
      alwaysReadonlyList.push('type');
    }
    if (!alwaysReadonlyList.includes('scoringCalculation')) {
      alwaysReadonlyList.push('scoringCalculation');
    }
    if (!alwaysReadonlyList.includes('timeLimit')) {
      alwaysReadonlyList.push('timeLimit');
    }

    // Courses can NOT be added or removed
    if (!alwaysReadonlyList.includes('courses')) {
      alwaysReadonlyList.push('courses');
    }

    // Groups can NOT be added or removed
    if (!alwaysReadonlyList.includes('groups')) {
      alwaysReadonlyList.push('groups');
    }

    // Start date can NOT be changed if it has already passed
    if (dynamicFormFields?.startDate && dateHasPassed(dynamicFormFields.startDate) && !alwaysReadonlyList.includes('startDate')) {
      alwaysReadonlyList.push('startDate');
    }
  }

  function isAlwaysReadonly(fieldName) {
    return alwaysReadonlyList.findIndex((f) => f === fieldName) !== -1;
  }

  const renderFields = (inputs, touched, errors, setFieldValue, props) => {
    let inGroup = false;
    if (inputs && inputs.length > 0) {
      return inputs.map((input, key) => {
        return (renderField(input, touched, errors, key, inGroup, setFieldValue, props));
      });
    }
  };
  const renderField = (input, touched, errors, key, isInGroup, setFieldValue, props) => {
    if (input.displayAsLabelIfDisabled && isAlwaysReadonly(input.fieldName)) { //action === 'update' &&
      return (
        <div className="form-group" key={`form-group-${key}`}>
          <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.fieldName}>
            {input.fieldLabel}
          </label>
          <Row>
            <Col>
              <label className={isInGroup ? 'campaign-view-group-smaller-text' : ''}>{getInputUserFriendlyValue(input, dynamicFormFields[input.fieldName])}
              </label>
            </Col>
          </Row>
        </div>
      );
    } else if (input.type === "list") {
      return (
        <div className="form-group" key={`form-group-${key}`}>
          <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.fieldName}>
            {input.fieldLabel}
            {input.isRequired ? " *" : ""}
          </label>
          {action === 'update' && campaign?.status === "published" ? (
            <Row>
              <Col>
                {dynamicFormFields?.[input.fieldName]?.length > 0 && dynamicFormFields?.[input.fieldName]?.map(function(item, i) {
                  if (input.fieldName === 'group' || showAllGroups || !showAllGroups && i < 10) {
                    return <Button className="btn btn-secondary" key={i}>{item.name}</Button>;
                  } else if (showAllGroups || !showAllGroups && i === 10) {
                    return <>
                      <Button className="btn btn-secondary show-all-btn" key={i} onClick={() => {
                        setShowAllGroups(true);
                      }}>Show All</Button>
                    </>;
                  } else {
                    return <></>;
                  }
                })}
              </Col>
            </Row>
          ) : input.url ? (
            <>
              <div className={errors?.[input.fieldName]?.length > 0 ? 'is-invalid' : ''}>
                <MultiSelectTypeahead
                  name={input.fieldName}
                  displayName={input.fieldLabel}
                  url={input.url}
                  // label="Search term"
                  placeholder="Search term"
                  selectAllLabel="Select all"
                  defaultValue={dynamicFormFields[input.fieldName]}
                  className={`form-control ${touched[input.fieldName] && errors[input.fieldName]
                    ? "is-invalid"
                    : ""
                  } ${touched[input.fieldName] && !errors[input.fieldName]
                    ? "is-valid"
                    : ""
                  }`}
                  onChange={(value) => {
                    if (input.fieldName === 'groups') {
                      setGroupsValue(value);
                      setFieldValue('groups', value.map((val) => val._id));
                    } else if (input.fieldName === 'courses') {
                      setCoursesValue(value);
                      setFieldValue('courses', value.map((val) => val._id));
                    }
                  }}
                  onFocus={() => {
                    touched[input.fieldName] = true;
                    if (input.fieldName === 'groups') {
                      if (groupsValue.length > groupsLimit.value) {
                        errors[input.fieldName] = `The number of groups has exceeded the limit of ${groupsLimit.displayValue}`;
                      } else if (!groupsValue.length || groupsValue.length === 0) {
                        errors[input.fieldName] = 'A minimum of 1 group is required';
                      } else {
                        delete errors[input.fieldName];
                      }
                    } else if (input.fieldName === 'courses') {
                      if (coursesValue.length > coursesLimit.value) {
                        errors[input.fieldName] = `The number of courses has exceeded the limit of ${coursesLimit.displayValue}`;
                      } else if (!coursesValue.length || coursesValue.length === 0) {
                        errors[input.fieldName] = 'A minimum of 1 course is required';
                      } else {
                        delete errors[input.fieldName];
                      }
                    }
                    props.validateForm();
                  }}
                  onBlur={() => {
                    touched[input.fieldName] = true;
                    if (input.fieldName === 'groups') {
                      if (groupsValue.length > groupsLimit.value) {
                        errors[input.fieldName] = `The number of groups has exceeded the limit of ${groupsLimit.displayValue}`;
                      } else if (!groupsValue.length || groupsValue.length === 0) {
                        errors[input.fieldName] = 'A minimum of 1 group is required';
                      } else {
                        delete errors[input.fieldName];
                      }
                    } else if (input.fieldName === 'courses') {
                      if (coursesValue.length > coursesLimit.value) {
                        errors[input.fieldName] = `The number of courses has exceeded the limit of ${coursesLimit.displayValue}`;
                      } else if (!coursesValue.length || coursesValue.length === 0) {
                        errors[input.fieldName] = 'A minimum of 1 course is required';
                      } else {
                        delete errors[input.fieldName];
                      }
                    }
                    props.validateForm();
                  }}
                />
              </div>
            </>
          ) : (
            <Field
              component="select"
              name={input.fieldName}
              disabled={
                isAlwaysReadonly(input.fieldName) ||
                (submitSuccess && submitSuccess.length > 0 ? true : false)
              }
              className={`form-control ${touched[input.fieldName] && errors[input.fieldName]
                ? "is-invalid"
                : ""
              } ${touched[input.fieldName] && !errors[input.fieldName]
                ? "is-valid"
                : ""
              }`}
            >
              {setSelectOptions(input)}
            </Field>
          )}
          <ErrorMessage
            component="div"
            name={input.fieldName}
            className="invalid-feedback"
          />
        </div>
      );
    } else if (input.type === "boolean") {
      return (
        <div className="form-group" key={key}>
          <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.fieldName}>
            {input.fieldLabel}
            {input.isRequired ? " *" : ""}
          </label>
          <div role="group" aria-labelledby={`${input.fieldName}-radio-group`}>
            {input.options.map((option, index) => {
              return (
                <label key={`${input.fieldName}-radio-${option.value}`}>
                  {option.name}
                  <Field
                    type={setInputType(input.type)}
                    name={input.fieldName}
                    value={option.value}
                    disabled={
                      isAlwaysReadonly(input.fieldName) ||
                      (submitSuccess && submitSuccess.length > 0 ? true : false)
                    }
                    placeholder={input.fieldLabel}
                    className={`form-control radio ${touched[input.fieldName] && errors[input.fieldName]
                      ? "is-invalid"
                      : ""
                    } ${touched[input.fieldName] && !errors[input.fieldName]
                      ? "is-valid"
                      : ""
                    }`}
                  />
                </label>
              );
            })}
          </div>
          <ErrorMessage
            component="div"
            name={input.fieldName}
            className="invalid-feedback"
          />
        </div>
      );
    } else if (input.type === 'textarea') {
      return (
        <div className="form-group" key={key}>
          <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.fieldName}>
            {input.fieldLabel}
            {input.isRequired ? " *" : ""}
          </label>
          <Field
            type={setInputType(input.type)}
            name={input.fieldName}
            as={'textarea'}
            disabled={
              isAlwaysReadonly(input.fieldName) ||
              (submitSuccess && submitSuccess.length > 0 ? true : false)
            }
            onKeyUp={handleChange}
            placeholder={input.fieldLabel}
            className={`form-control ${touched[input.fieldName] && errors[input.fieldName]
              ? "is-invalid"
              : ""
            } ${touched[input.fieldName] && !errors[input.fieldName]
              ? "is-valid"
              : ""
            }`}
          />
          <ErrorMessage
            component="div"
            name={input.fieldName}
            className="invalid-feedback"
          />
        </div>
      );
    } else if (input.type === "fieldGroup") {
      return (
        <div className="form-group" key={key}>
          {input.groupLabel && (
            <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.groupName}>
              {input.groupLabel}
              {input.isRequired ? " *" : ""}
            </label>
          )}
          <div className='row'>
            {input.groupedFields.map((groupInput, index) => {
              let inGroup = true;
              return (
                <div className={input.colClasses} key={`${input.groupName}-field-group-${index}`}>
                  <div key={`${key}-${index}`}>
                    {renderField(groupInput, touched, errors, index, inGroup)}
                  </div>
                </div>
              );
            })}
          </div >
        </div >
      );
    } else {
      return (
        <div className="form-group" key={key}>
          <label className={isInGroup ? 'campaign-view-group-label' : 'campaign-view-label'} htmlFor={input.fieldName}>
            {input.fieldLabel}
            {input.isRequired ? " *" : ""}
          </label>
          <Field
            type={setInputType(input.type)}
            name={input.fieldName}
            disabled={
              isAlwaysReadonly(input.fieldName) ||
              (submitSuccess && submitSuccess.length > 0 ? true : false)
            }
            onKeyUp={handleChange}
            placeholder={input.fieldLabel}
            className={`form-control ${touched[input.fieldName] && errors[input.fieldName]
              ? "is-invalid"
              : ""
            } ${touched[input.fieldName] && !errors[input.fieldName]
              ? "is-valid"
              : ""
            }`}
          />
          <ErrorMessage
            component="div"
            name={input.fieldName}
            className="invalid-feedback"
          />
        </div>
      );
    }
  };

  const redirectOnSuccess = () => {
    setTimeout(() => {
      history.push({ pathname: `/campaigns/${project}/${campaignId}` });
    }, 500);
  };

  const upsertCampaign = async(values, setFieldValue) => {
    try {
      values.action = action;
      values._id = campaign._id;
      const apiCallConfig = {
        url: `/admin/${project}/campaigns/${action === 'update' ? campaign._id + '/' : ''}${action}`,
        data: values,
      };
      setSubmittingForm(true);
      let response;
      if (action === 'update') {
        response = await API.put(apiCallConfig);
      } else {
        response = await API.post(apiCallConfig);
      }
      if (response) {
        if (response._id) {
          if (!campaignId && response._id) {
            campaignId = response._id;
          }
          if (action === "update") {
            // success
            setSubmitSuccess("Campaign updated successfully");
            redirectOnSuccess();
          } else if (action === "create") {
            setSubmitSuccess("Campaign created successfully");
            redirectOnSuccess();
          } else if (response.message) {
            setSubmitError(response.message);
          } else {
            setSubmitError("Something went wrong");
          }
        } else {
          setSubmitError("Something went wrong");
        }
        setSubmittingForm(false);
      }

    } catch (err) {
      if (err.response) {
        setSubmitError(err.response.data.message);
      } else {
        setSubmitError("An error occurred, please try again");
      }
      setSubmittingForm(false);
    }
  };

  const handleSubmit = async(values, setFieldValue) => {
    setSubmitSuccess(null);
    setSubmitError(null);
    if (values.courses.length > 0) {
      values.courses = values.courses.map((course) => {
        return course._id ? course._id : course;
      });
    }
    if (values.groups.length > 0) {
      values.groups = values.groups.map((group) => {
        return group._id ? group._id : group;
      });
    }
    await upsertCampaign(values, setFieldValue);
  };

  const handleCancel = (callback) => {
    if (callback) {
      callback();
    }
    if (action === "update") {
      history.push({ pathname: `/campaigns/${project}/${campaignId}` });
    } else {
      history.push({ pathname: `/campaigns/${project}` });
    }
  };

  // Determine if the number of courses multiplied by users has exceeded the limit of ${courseGroupMultiplierLimit.value}
  const isFormCourseGroupLimitExceeded = () => {
    if (groupsValue?.length > 0 && coursesValue?.length > 0) {
      let numberOfGroupUsers = groupsValue.reduce((accumulator, group) => {
        return accumulator + group.usersCount;
      }, 0);
      if (numberOfGroupUsers * coursesValue.length > courseGroupMultiplierLimit.value) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  };

  useEffect(() => {
    if (isFormCourseGroupLimitExceeded()) {
      setSubmitError(`The number of courses multiplied by users has exceeded the limit of ${courseGroupMultiplierLimit.value}`);
    } else {
      setSubmitError("");
    }
  }, [groupsValue, coursesValue]);

  if (action && action === 'update' && !campaign?._id) {
    return (
      <React.Fragment>
        <Row>
          <Col>{loading()}</Col>
        </Row>
      </React.Fragment>
    );
  }

  return (
    <div className="campaign-view">
      <Row>
        <Col>
          <Breadcrumb as="nav" bsPrefix="breadcrumb ph-admin__breadcrumb">
            <LinkContainer to={`/campaigns/${project}`}>
              <Breadcrumb.Item bsPrefix="breadcrumb-item clickable">
                Campaigns
              </Breadcrumb.Item>
            </LinkContainer>
            <LinkContainer
              to={
                formMode === "create"
                  ? `campaigns/${project}`
                  : `/campaigns/${project}/${campaignId}`
              }
            >
              <Breadcrumb.Item active>
                {formMode === "create" ? "New Campaign" : campaignName}
              </Breadcrumb.Item>
            </LinkContainer>
          </Breadcrumb>
        </Col>
      </Row>

      <Row>
        <Col>
          <Formik
            validateOnChange
            validateOnBlur
            initialValues={dynamicFormFields}
            validationSchema={Yup.object().shape(dynamicFormValidation)}
            onSubmit={(values, { setSubmitting, setFieldValue }) => {
              setSubmitting(true);
              handleSubmit(values, setFieldValue);
            }}
          >
            {({ touched, errors, isSubmitting, setFieldValue, ...props }) => (
              <Form noValidate ref={formRef}>
                {renderFields(campaignFields, touched, errors, setFieldValue, props)}

                {submitError && <Alert variant={"danger"}>{submitError}</Alert>}
                {submitSuccess && (
                  <Alert variant={"success"}>{submitSuccess}</Alert>
                )}

                <div className="form-group">
                  {submittingForm ? (
                    <Spinner animation="border" role="status">
                      <span className="sr-only">Loading...</span>
                    </Spinner>
                  ) : (
                    <>
                      <button
                        className={`mb-2 btn btn-outline-secondary ${submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                          ? "hide"
                          : ""
                        }`}
                        onClick={() => {
                          handleCancel(() => {
                            props.handleReset();
                          });
                        }}
                        style={{ marginRight: "5px" }}
                        disabled={
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                        }
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className={`mb-2 btn btn-outline-primary ${submittingForm ||
                          (submitSuccess && submitSuccess.length > 0)
                          ? "hide"
                          : ""
                        }`}
                        disabled={
                          submittingForm ||
                          (submitSuccess && submitSuccess.length > 0
                            ? true
                            : false)
                        }
                      >
                        Save
                      </button>
                    </>
                  )
                  }
                </div >
              </Form >
            )}
          </Formik >
        </Col >
      </Row >
    </div >
  );
}